import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { staffAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import StaffNav from '../../components/StaffNav';
import { Users, Loader2, Copy, Share2, CheckCircle, Clock, XCircle, DollarSign, Gift, User, Calendar } from 'lucide-react';

export default function MyReferrals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => { loadReferrals(); }, []);

  const loadReferrals = async () => {
    try { const res = await staffAPI.getMyReferrals(); setReferrals(res.data.data || []); }
    catch { toast.error('Failed to load referrals'); }
    finally { setLoading(false); }
  };

  const handleCopy = () => {
    if (!user?.referralCode) return;
    navigator.clipboard.writeText(user.referralCode).catch(() => {
      const ta = document.createElement('textarea'); ta.value = user.referralCode;
      document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
    });
    setCopied(true); toast.success('Copied!'); setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    const msg = `Join ShramSena! Use my referral code: ${user?.referralCode} at shramsena.in`;
    if (navigator.share) { navigator.share({ title: 'ShramSena Referral', text: msg }).catch(() => {}); }
    else { window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank'); }
  };

  const badge = (status) => {
    const m = { APPROVED:'bg-green-100 text-green-700', REJECTED:'bg-red-100 text-red-700', PENDING:'bg-yellow-100 text-yellow-700', PAID:'bg-blue-100 text-blue-700' };
    const c = m[status] || 'bg-gray-100 text-gray-700';
    return <span className={"px-2 py-0.5 rounded-full text-xs font-semibold " + c}>{status}</span>;
  };

  const totalEarned = referrals.reduce((s, r) => r.incentiveAmount && (r.incentiveStatus==='PAID'||r.incentiveStatus==='APPROVED') ? s+parseFloat(r.incentiveAmount) : s, 0);
  const paidAmount  = referrals.reduce((s, r) => r.incentiveAmount && r.incentiveStatus==='PAID' ? s+parseFloat(r.incentiveAmount) : s, 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="h-10 w-10 animate-spin text-teal-600" /></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <StaffNav title="My Referrals" />
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">

        <div className="bg-gradient-to-r from-purple-600 to-teal-600 rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-20"
            style={{ background: 'radial-gradient(ellipse at 80% 50%, white 0%, transparent 70%)' }} />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-white/70 text-xs font-bold uppercase tracking-wide">Your Referral Code</p>
              <p className="text-3xl font-black text-white font-mono mt-1">{user?.referralCode}</p>
              <p className="text-white/60 text-xs mt-1">Earn ₹50 for every successful referral</p>
            </div>
            <div className="flex gap-2">
              <button onClick={handleCopy}
                className={"flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition " + (copied ? 'bg-green-500 text-white' : 'bg-white/20 text-white hover:bg-white/30')}>
                {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={handleShare} className="flex items-center gap-1.5 bg-white text-purple-700 px-4 py-2 rounded-xl text-sm font-bold hover:bg-white/90 transition">
                <Share2 className="h-4 w-4" /> Share
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { val: referrals.length, label: 'Total',        color: 'text-slate-800' },
            { val: referrals.filter(r=>r.referralStatus==='APPROVED').length, label: 'Approved', color: 'text-green-600' },
            { val: '₹' + totalEarned, label: 'Total Earned', color: 'text-blue-600' },
            { val: '₹' + paidAmount,  label: 'Paid Out',     color: 'text-purple-600' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
              <p className={"text-2xl font-black " + s.color}>{s.val}</p>
              <p className="text-xs text-gray-400 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {referrals.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
            <Gift className="h-14 w-14 text-purple-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-600">No referrals yet</h3>
            <p className="text-gray-400 text-sm mt-2 max-w-xs mx-auto">Share your code with fellow healthcare professionals and earn ₹50 each time!</p>
            <button onClick={handleShare} className="mt-4 inline-flex items-center gap-2 bg-purple-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-purple-700 transition">
              <Share2 className="h-4 w-4" /> Share Your Code
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Referral History</h3>
            {referrals.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-800">{r.referredName}</p>
                      <p className="text-sm text-gray-400">{r.referredPhone}</p>
                    </div>
                  </div>
                  {badge(r.referralStatus)}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <Calendar className="h-3.5 w-3.5" />
                    {r.referredAt ? new Date(r.referredAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : 'N/A'}
                  </span>
                  {r.incentiveAmount ? (
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-green-700 text-sm">₹{r.incentiveAmount}</span>
                      {badge(r.incentiveStatus)}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">{r.referralStatus === 'PENDING' ? 'Awaiting approval' : 'No incentive'}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="font-bold text-slate-800 mb-4">How Referrals Work</h3>
          <div className="space-y-3">
            {[['Share Your Code','Share your unique referral code with fellow healthcare professionals'],
              ['They Register','Your friend signs up on ShramSena using your referral code'],
              ['Both Get Approved','Once both profiles are approved, the referral is reviewed'],
              ['Earn ₹50','You earn a ₹50 bonus credited to your account']].map(([t,d],i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 text-xs font-black shrink-0">{i+1}</div>
                <div><p className="font-semibold text-sm text-slate-800">{t}</p><p className="text-xs text-gray-400">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}